import { useState } from "react";
import { useNavigate } from "react-router";
import { addMonths, isAfter, parseISO } from "date-fns";
import { ArrowLeft, ArrowRight, Check, Info, MapPin } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  PageHeader,
  VButton,
  VCheckbox,
  VField,
  VFileUpload,
  VInput,
  VRadioGroup,
  VStepper,
  VTextarea,
} from "@/components/ui";
import type {
  ComplianceChoice,
  ExtensionChoice,
  PolePresence,
  V2Document,
} from "@/v2/shared/model";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useCreateRequest } from "@/v2/data/mutations";
import { reverseGeocode } from "@/v2/data/geocode";
import { MapPicker } from "@/v2/shared/mapPicker";

const STEPS = [
  { label: "Aanvrager" },
  { label: "Periode" },
  { label: "Locatie & plaatsbezoek" },
  { label: "Bijkomende info" },
  { label: "Motivatie" },
  { label: "Indienen" },
] as const;

type UploadedFile = { id: string; name: string; size: number };

type WizardErrors = Partial<Record<string, string>>;

export function RequestWizardPage(): React.JSX.Element {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const createRequest = useCreateRequest();
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<WizardErrors>({});

  // Onderdeel 1 — aanvrager (voorgevuld uit het Entra-profiel; bewerkbaar).
  const [lastName, setLastName] = useState(user.lastName);
  const [firstName, setFirstName] = useState(user.firstName);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone);
  const [service, setService] = useState(user.service);
  const [serviceContact, setServiceContact] = useState(user.serviceContact);

  // Onderdeel 2 — periode & verlenging.
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [extension, setExtension] = useState<ExtensionChoice | undefined>(undefined);

  // Onderdeel 3 — locatie (kaartpunt verplicht) & plaatsbezoek. Het adres
  // wordt afgeleid uit het gekozen kaartpunt (reverse geocoding), niet meer
  // manueel ingevuld.
  const [address, setAddress] = useState("");
  const [mapPoint, setMapPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [locationDescription, setLocationDescription] = useState("");
  const [polePresence, setPolePresence] = useState<PolePresence | undefined>(undefined);
  const [files, setFiles] = useState<ReadonlyArray<UploadedFile>>([]);
  const [environmentalFactors, setEnvironmentalFactors] = useState("");

  // Onderdeel 4 — evenement & parkeerborden.
  const [event, setEvent] = useState("");
  const [eventInfo, setEventInfo] = useState("");
  const [parkingSigns, setParkingSigns] = useState("");

  // Onderdeel 5 — motivatie & conformiteit.
  const [reason, setReason] = useState("");
  const [pvNumber, setPvNumber] = useState("");
  const [specialAuthorization, setSpecialAuthorization] = useState("");
  const [addedValue, setAddedValue] = useState("");
  const [compliance, setCompliance] = useState<ComplianceChoice | undefined>(undefined);

  // Onderdeel 6 — titel & bevestiging.
  const [title, setTitle] = useState("");
  const [qualityConfirmed, setQualityConfirmed] = useState(false);

  function validateStep(index: number): WizardErrors {
    const next: WizardErrors = {};
    if (index === 0) {
      if (!lastName.trim()) next.lastName = "Achternaam is verplicht.";
      if (!firstName.trim()) next.firstName = "Voornaam is verplicht.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Geef een geldig e-mailadres op.";
      if (!phone.trim()) next.phone = "Telefoonnummer is verplicht.";
      if (!service.trim()) next.service = "Dienst is verplicht.";
      if (!serviceContact.trim()) next.serviceContact = "Contactgegevens dienst zijn verplicht.";
    }
    if (index === 1) {
      if (!startDate) next.startDate = "Begindatum is verplicht.";
      if (!endDate) next.endDate = "Einddatum is verplicht.";
      if (startDate && endDate) {
        const start = parseISO(startDate);
        const end = parseISO(endDate);
        if (isAfter(start, end)) {
          next.endDate = "De einddatum mag niet vóór de begindatum liggen.";
        } else if (isAfter(end, addMonths(start, 3))) {
          next.endDate = "De maximale duurtijd van een inzet is drie maanden.";
        }
      }
      if (!extension) next.extension = "Geef aan of verlenging mogelijk is.";
    }
    if (index === 2) {
      if (!mapPoint) next.mapPoint = "Duid de plaatsingslocatie aan op de kaart.";
      if (files.length === 0) {
        next.files = "Minstens één plaatsbezoek-foto of plannetje is verplicht.";
      }
    }
    if (index === 4) {
      if (!reason.trim()) next.reason = "Reden van aanvraag is verplicht.";
    }
    if (index === 5) {
      if (!qualityConfirmed) next.qualityConfirmed = "Bevestiging is verplicht vóór indiening.";
    }
    return next;
  }

  function goNext(): void {
    const stepErrors = validateStep(step);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function goBack(): void {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
  }

  // Kaartpunt gekozen: coördinaten bewaren en het adres afleiden (reverse
  // geocoding, met terugval op de coördinaten).
  async function handleMapSelect(next: { lat: number; lng: number }): Promise<void> {
    setMapPoint(next);
    setErrors((prev) => ({ ...prev, mapPoint: undefined }));
    setAddressLoading(true);
    const resolved = await reverseGeocode(next.lat, next.lng);
    setAddress(resolved ?? `Locatie op kaart (${next.lat.toFixed(5)}, ${next.lng.toFixed(5)})`);
    setAddressLoading(false);
  }

  function handleSubmit(): void {
    const stepErrors = validateStep(5);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0 || !mapPoint || !extension) return;

    const documents: ReadonlyArray<V2Document> = files.map((f, idx) => ({
      id: f.id,
      filename: f.name,
      kind: idx === 0 ? "siteVisitPhoto" : "other",
      sizeLabel: `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
    }));

    createRequest.mutate(
      {
        title: title.trim() || `Camera-aanvraag ${address}`,
        requesterId: user.id,
        requesterLastName: lastName,
        requesterFirstName: firstName,
        requesterEmail: email,
        requesterPhone: phone,
        requesterService: service,
        requesterServiceContact: serviceContact,
        startDate,
        startTime: startTime || undefined,
        endDate,
        extension,
        address,
        locationLat: mapPoint.lat,
        locationLng: mapPoint.lng,
        locationDescription,
        polePresence: polePresence ?? "notApplicable",
        environmentalFactors,
        event,
        eventInfo,
        parkingSigns,
        reason,
        pvNumber,
        specialAuthorization,
        addedValue,
        compliance: compliance ?? "unanswered",
        qualityConfirmed,
        documents,
      },
      { onSuccess: (created) => navigate(`/aanvragen/${created.id}`, { replace: true }) },
    );
  }

  return (
    <div className="space-y-6">
      <VButton variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="size-4" aria-hidden />
        Terug
      </VButton>

      <PageHeader
        title="Nieuwe aanvraag"
        subtitle="Aanvraag voor de inzet van een mobiele camera. Eén aanvraag per toezichtsituatie."
      />

      <Card padding="md" className="flex items-start gap-3 border-info-200 bg-info-50">
        <Info className="mt-0.5 size-4 shrink-0 text-info-600" aria-hidden />
        <Caption tone="default">
          Reken op een standaardtermijn van <strong>minimum 8 werkdagen</strong> tussen indiening en
          effectieve plaatsing. Een plaatsbezoek is verplicht. Aanvragen zonder plaatsbezoek worden
          niet in behandeling genomen.
        </Caption>
      </Card>

      <VStepper steps={STEPS} currentStep={step} onStepClick={(i) => setStep(i)} />

      <Card padding="lg" className="space-y-5">
        {step === 0 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <VField label="Achternaam" htmlFor="w-lastname" required error={errors.lastName}>
              <VInput id="w-lastname" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </VField>
            <VField label="Voornaam" htmlFor="w-firstname" required error={errors.firstName}>
              <VInput id="w-firstname" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </VField>
            <VField label="E-mailadres" htmlFor="w-email" required error={errors.email}>
              <VInput id="w-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </VField>
            <VField label="Telefoonnummer" htmlFor="w-phone" required error={errors.phone}>
              <VInput id="w-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </VField>
            <VField label="Dienst" htmlFor="w-service" required error={errors.service}>
              <VInput id="w-service" value={service} onChange={(e) => setService(e.target.value)} />
            </VField>
            <VField
              label="Contactgegevens dienst"
              htmlFor="w-servicecontact"
              required
              hint="E-mailadres (bij voorkeur functioneel) en/of telefoonnummer van de dienst."
              error={errors.serviceContact}
            >
              <VInput
                id="w-servicecontact"
                value={serviceContact}
                onChange={(e) => setServiceContact(e.target.value)}
              />
            </VField>
            <Caption tone="muted" className="sm:col-span-2">
              Voorgevuld vanuit je profiel. Pas aan waar nodig.
            </Caption>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <VField label="Begindatum inzet" htmlFor="w-start" required error={errors.startDate}>
                <VInput id="w-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </VField>
              <VField label="Beginuur inzet" htmlFor="w-starttime" hint="Optioneel (uu:mm).">
                <VInput id="w-starttime" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </VField>
              <VField
                label="Einddatum inzet"
                htmlFor="w-end"
                required
                hint="Maximale duurtijd van een inzet is drie maanden."
                error={errors.endDate}
              >
                <VInput id="w-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </VField>
            </div>
            <VField label="Verlenging mogelijk" required error={errors.extension}>
              <VRadioGroup<ExtensionChoice>
                name="extension"
                value={extension}
                onChange={setExtension}
                options={[
                  { value: "possible", label: "Ja, verlenging mogelijk (einddatum kan opschuiven)" },
                  { value: "fixed", label: "Nee, vaste einddatum (inzet eindigt strikt op de einddatum)" },
                ]}
              />
            </VField>
            <Card padding="md" className="border-dashed">
              <Caption tone="muted">
                Standaardplaatsing is één maand, met maximaal twee verlengingen (samen max. drie
                maanden). Je bent zelf verantwoordelijk voor tijdige opvolging: meld een gewenste
                verlenging uiterlijk tien dagen vóór het verstrijken van de inzetperiode.
              </Caption>
            </Card>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            <VField
              label="Locatie op kaart"
              required
              hint="Klik op de kaart of versleep de marker om de exacte plaatsingslocatie vast te leggen. Het adres wordt automatisch afgeleid."
              error={errors.mapPoint}
            >
              <MapPicker point={mapPoint} onSelect={handleMapSelect} />
            </VField>
            {mapPoint ? (
              <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                <MapPin className="size-4 shrink-0 text-primary-600" aria-hidden />
                <Caption tone="default">
                  {addressLoading ? "Adres bepalen…" : address}
                </Caption>
              </div>
            ) : null}
            <VField
              label="Locatie en kijkrichting"
              htmlFor="w-locdesc"
              hint="Beschrijf de kijkrichting(en) en wat in beeld moet komen (persoon/plein/gebouw/rijbaan/deur)."
            >
              <VTextarea
                id="w-locdesc"
                rows={3}
                value={locationDescription}
                onChange={(e) => setLocationDescription(e.target.value)}
              />
            </VField>
            <VField label="Aanwezigheid paal" hint="Is er een paal (verlichtingspaal, verkeerslicht, …) aanwezig?">
              <VRadioGroup<PolePresence>
                name="pole"
                value={polePresence}
                onChange={setPolePresence}
                orientation="horizontal"
                options={[
                  { value: "yes", label: "Ja" },
                  { value: "no", label: "Nee" },
                  { value: "unsure", label: "Weet ik niet zeker" },
                  { value: "notApplicable", label: "Niet van toepassing" },
                ]}
              />
            </VField>
            <VField
              label="Plannetje en foto's plaatsing"
              required
              hint="Plannetje met locatie en kijkrichting + foto's van de actuele situatie. Max. 5 bestanden, 10 MB per bestand."
              error={errors.files}
            >
              <VFileUpload
                files={files}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.mp3"
                hint="Word, Excel, PPT, PDF, afbeelding, video of audio · max. 5 bestanden"
                onAdd={(added) =>
                  setFiles((prev) =>
                    [
                      ...prev,
                      ...added.map((f, i) => ({
                        id: `up-${prev.length + i}-${f.name}`,
                        name: f.name,
                        size: f.size,
                      })),
                    ].slice(0, 5),
                  )
                }
                onRemove={(id) => setFiles((prev) => prev.filter((f) => f.id !== id))}
              />
            </VField>
            <VField label="Omgevingsfactoren of bijzonderheden" htmlFor="w-env">
              <VTextarea
                id="w-env"
                rows={2}
                value={environmentalFactors}
                onChange={(e) => setEnvironmentalFactors(e.target.value)}
              />
            </VField>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-4">
            <VField label="Evenement" htmlFor="w-event" hint="Naam van het evenement, indien van toepassing.">
              <VInput id="w-event" value={event} onChange={(e) => setEvent(e.target.value)} />
            </VField>
            <VField
              label="Info evenement"
              htmlFor="w-eventinfo"
              hint="Contactgegevens van een verantwoordelijke en eventuele restricties (bv. opbouw festival)."
            >
              <VTextarea id="w-eventinfo" rows={3} value={eventInfo} onChange={(e) => setEventInfo(e.target.value)} />
            </VField>
            <VField
              label="Info parkeerborden"
              htmlFor="w-parking"
              hint="Straatnaam en huisnummers (van & tot). Je voorziet de plaatsing van parkeerborden zelf."
            >
              <VInput id="w-parking" value={parkingSigns} onChange={(e) => setParkingSigns(e.target.value)} />
            </VField>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="space-y-4">
            <VField
              label="Reden van aanvraag camera"
              htmlFor="w-reason"
              required
              hint="Bv. overlast, observatie, …"
              error={errors.reason}
            >
              <VInput id="w-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </VField>
            <div className="grid gap-4 sm:grid-cols-2">
              <VField
                label="PV-nummer, dossiernummer of dossiernaam"
                htmlFor="w-pv"
                hint="Externe referentie, indien van toepassing."
              >
                <VInput id="w-pv" value={pvNumber} onChange={(e) => setPvNumber(e.target.value)} />
              </VField>
              <VField
                label="Nummer bijzondere machtiging"
                htmlFor="w-auth"
                hint="Parket/OR of andere (bv. BOM, BIM), indien beschikbaar."
              >
                <VInput
                  id="w-auth"
                  value={specialAuthorization}
                  onChange={(e) => setSpecialAuthorization(e.target.value)}
                />
              </VField>
            </div>
            <VField
              label="Meerwaarde inzet camera"
              htmlFor="w-value"
              hint="Controleer ook of er op locatie een stadscamera aanwezig is die met een tijdelijke vaste preset kan worden ingezet."
            >
              <VTextarea id="w-value" rows={3} value={addedValue} onChange={(e) => setAddedValue(e.target.value)} />
            </VField>
            <VField
              label="Conformiteit wetgeving"
              hint="Houdt het geplande cameratoezicht rekening met privacy en wettelijke verbodsbepalingen/beperkingen?"
            >
              <VRadioGroup<ComplianceChoice>
                name="compliance"
                value={compliance}
                onChange={setCompliance}
                orientation="horizontal"
                options={[
                  { value: "yes", label: "Ja" },
                  { value: "no", label: "Nee" },
                ]}
              />
            </VField>
          </div>
        ) : null}

        {step === 5 ? (
          <div className="space-y-4">
            <VField
              label="Titel aanvraag"
              htmlFor="w-title"
              hint="Wordt gebruikt voor alle verdere communicatie over de aanvraag."
            >
              {/* CB-1: placeholder als leidraad voor een uniforme titel. */}
              <VInput
                id="w-title"
                placeholder="Straat - Startdatum - Dossier/Reden"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </VField>
            <VField label="Kwaliteit en inhoud" required error={errors.qualityConfirmed}>
              <VCheckbox
                checked={qualityConfirmed}
                onCheckedChange={(c) => setQualityConfirmed(c === true)}
                label="Ik bevestig de kwaliteit en de inhoud van de beelden te zullen nakijken conform het concept mobiele camera's en hun relevantie."
              />
            </VField>
            <SummaryCard
              items={[
                ["Aanvrager", `${firstName} ${lastName} · ${service}`],
                ["Periode", startDate && endDate ? `${startDate} → ${endDate}` : "—"],
                ["Adres", address || "-"],
                ["Reden", reason || "-"],
                ["Bijlagen", `${files.length} bestand(en)`],
              ]}
            />
          </div>
        ) : null}

        <div className="flex items-center justify-between border-t border-border pt-4">
          <VButton variant="outline" onClick={goBack} disabled={step === 0}>
            <ArrowLeft className="size-4" aria-hidden />
            Vorige
          </VButton>
          {step < STEPS.length - 1 ? (
            <VButton onClick={goNext}>
              Volgende
              <ArrowRight className="size-4" aria-hidden />
            </VButton>
          ) : (
            <VButton onClick={handleSubmit} disabled={createRequest.isPending}>
              <Check className="size-4" aria-hidden />
              {createRequest.isPending ? "Indienen…" : "Aanvraag indienen"}
            </VButton>
          )}
        </div>
      </Card>
    </div>
  );
}

function SummaryCard({
  items,
}: {
  items: ReadonlyArray<readonly [string, string]>;
}): React.JSX.Element {
  return (
    <Card padding="md" className="divide-y divide-border bg-muted/30">
      {items.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[8rem_1fr] gap-2 py-1.5">
          <Caption tone="muted">{label}</Caption>
          <Body>{value}</Body>
        </div>
      ))}
    </Card>
  );
}
